import { UPDATE_RELOAD_FLAG } from "./cachePolicy";
import { hasUnsavedWork } from "./unsavedWork";

/** Show/focus/pointer/key checks share this gap. The open-tab poll is slower. */
const UPDATE_CHECK_GAP_MS = 60 * 1000;
const UPDATE_CHECK_OPEN_MS = 5 * 60 * 1000;

/** A worker we can tell apart from the next install. scriptURL stays /sw.js. */
export type WaitingWorker = {
  state: string;
  addEventListener: (type: "statechange", listener: () => void) => void;
};

export type AppUpdateRegistration = {
  update: () => Promise<void> | void;
  waiting?: WaitingWorker | null;
  installing?: WaitingWorker | null;
  active?: WaitingWorker | null;
};

export type UpdateWorker = {
  addEventListener: (
    type: "waiting" | "controlling",
    listener: (event: {
      isUpdate?: boolean;
      wasWaitingBeforeRegister?: boolean;
      worker?: WaitingWorker;
    }) => void,
  ) => void;
  messageSkipWaiting: () => void;
  register: (options?: {
    immediate?: boolean;
  }) => Promise<AppUpdateRegistration | undefined>;
};

export type AppUpdateSession = {
  acceptUpdate: () => void;
};

/**
 * Shows a prompt when a new worker starts waiting while the tab is open.
 * A worker already waiting at boot is activated immediately when the page
 * has no unsaved work: SKIP_WAITING, then one reload on the first
 * controllerchange or activated state. Unsaved work keeps the bar.
 * "Vernieuwen" uses the same single reload. A second controlling event
 * does not reload again.
 */
export function startAppUpdate(options: {
  createWorker: () => UpdateWorker;
  reload: () => void;
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem">;
  onNeedRefresh: () => void;
  /** Hide the bar once that waiting worker is gone or has taken control. */
  onDismiss?: () => void;
  /**
   * True on the load that just reloaded for an update. Only the worker
   * already waiting at register time is ignored; a later one still prompts.
   */
  suppressed?: boolean;
  /** `import.meta.env.MODE`, overridable in tests. */
  mode?: string;
  locationSearch?: string;
  /**
   * Test override. Production uses `navigator.serviceWorker.controller`.
   * `registration.active` is not the page controller: a hard refresh keeps
   * the worker active while this document stays uncontrolled.
   */
  pageControlled?: () => boolean;
  /**
   * While the tab stays open, call `registration.update()` on show, focus,
   * pointer, key, pageshow, and online (at most once a minute) and on a
   * 5-minute interval. Default false in tests. A waiting worker still only
   * shows the bar; this never reloads the page.
   */
  watch?: boolean;
  /**
   * True when a reload would drop what the user is doing. Default reads the
   * document (open dialog, dirty field, save in flight).
   */
  hasUnsavedState?: () => boolean;
}): AppUpdateSession {
  const mode = options.mode ?? import.meta.env.MODE;
  const flagged = options.storage.getItem(UPDATE_RELOAD_FLAG) === "1";
  if (flagged) options.storage.removeItem(UPDATE_RELOAD_FLAG);
  const reloaded = options.suppressed === true || flagged;
  const worker = options.createWorker();
  let reloadStarted = false;
  let accepted = false;
  // Vernieuwen is the user confirming a reload. The automatic boot reload
  // is not, so it still has to see unsaved work that appeared after the decision.
  let userAccepted = false;
  // Set when a reload was refused because the page became dirty after the
  // decision to activate. Keeps the bar up when `controlling` arrives next.
  let heldForUnsaved = false;
  let registrationRef: AppUpdateRegistration | undefined;
  let promptedWorker: WaitingWorker | undefined;
  // Set only for the worker currently prompting. A later install resets them.
  let sawControl = false;
  let leftWaiting = false;
  // Workers already waiting when this reloaded page registered. Later ones differ.
  const skipped = new Set<WaitingWorker>();
  const armed = new Set<WaitingWorker>();
  const watched = new Set<WaitingWorker>();
  const bootHandled = new Set<WaitingWorker>();

  const unsaved = () => {
    try {
      return (options.hasUnsavedState ?? hasUnsavedWork)();
    } catch {
      return true;
    }
  };

  const pageControlled = () =>
    options.pageControlled?.() ??
    Boolean(
      typeof navigator !== "undefined" && navigator.serviceWorker?.controller,
    );

  const reloadOnce = () => {
    if (reloadStarted) return;
    if (options.storage.getItem(UPDATE_RELOAD_FLAG) !== "1") return;
    reloadStarted = true;
    options.reload();
  };

  const reloadForUpdate = () => {
    // A note can become dirty after SKIP_WAITING and before this reload.
    if (!userAccepted && unsaved()) {
      heldForUnsaved = true;
      accepted = false;
      options.onNeedRefresh();
      return;
    }
    heldForUnsaved = false;
    options.storage.setItem(UPDATE_RELOAD_FLAG, "1");
    reloadOnce();
  };

  const dismiss = () => {
    if (accepted || reloadStarted) return;
    options.onDismiss?.();
  };

  // Unknown until register() resolves. An empty slot after that means the
  // worker we prompted for has already left `waiting`.
  const liveWaiting = () => {
    if (!registrationRef) {
      const state = promptedWorker?.state;
      return state === "installed" || state === "installing";
    }
    const slot = registrationRef.waiting;
    return Boolean(slot && slot.state === "installed");
  };

  const alreadyApplied = () => {
    if (!promptedWorker) return false;
    if (sawControl || leftWaiting) return true;
    const state = promptedWorker.state;
    if (state === "activated" || state === "redundant") return true;
    return Boolean(registrationRef) && !liveWaiting();
  };

  const watchWorker = (target: WaitingWorker) => {
    if (watched.has(target)) return;
    watched.add(target);
    target.addEventListener("statechange", () => {
      if (target !== promptedWorker) return;
      if (target.state === "installed" || target.state === "installing") {
        return;
      }
      // No longer waiting. `activating` has not taken control yet.
      if (target.state === "activating") {
        dismiss();
        return;
      }
      leftWaiting = true;
      if (accepted) reloadForUpdate();
      else if (heldForUnsaved) options.onNeedRefresh();
      else dismiss();
    });
  };

  const showPrompt = (
    waiting?: WaitingWorker,
    wasWaitingBeforeRegister?: boolean,
  ) => {
    if (accepted || reloadStarted) return;
    if (reloaded && wasWaitingBeforeRegister) {
      if (waiting) skipped.add(waiting);
      return;
    }
    if (waiting && skipped.has(waiting)) return;
    // workbox-window emits `waiting` for a worker that was already waiting
    // at register(), including on an uncontrolled document (hard refresh).
    // That worker then activates by itself. It is not an update this page
    // still has to apply.
    if (waiting && !pageControlled()) return;
    if (waiting && waiting.state !== "installed") return;
    if (waiting && waiting !== promptedWorker) {
      sawControl = false;
      leftWaiting = false;
    }
    if (waiting) {
      promptedWorker = waiting;
      watchWorker(waiting);
    }
    options.onNeedRefresh();
  };

  const keepBar = (waiting: WaitingWorker) => {
    promptedWorker = waiting;
    watchWorker(waiting);
    options.onNeedRefresh();
  };

  // A worker that survived into this load. One attempt per worker. The
  // session flag from the reload that follows stops the next load from
  // trying again.
  const activateOnBoot = (waiting: WaitingWorker) => {
    if (bootHandled.has(waiting) || skipped.has(waiting)) return;
    bootHandled.add(waiting);
    if (reloaded) {
      skipped.add(waiting);
      return;
    }
    if (waiting.state !== "installed") return;
    if (unsaved()) {
      keepBar(waiting);
      return;
    }
    accepted = true;
    promptedWorker = waiting;
    watchWorker(waiting);
    worker.messageSkipWaiting();
  };

  worker.addEventListener("waiting", (event) => {
    if (event.wasWaitingBeforeRegister && event.worker) {
      activateOnBoot(event.worker);
      return;
    }
    showPrompt(event.worker, event.wasWaitingBeforeRegister);
  });

  worker.addEventListener("controlling", (event) => {
    if (event.worker && promptedWorker && event.worker !== promptedWorker) {
      return;
    }
    // First-install claim, before any waiting update, is not this prompt.
    if (!accepted && !promptedWorker) return;
    if (!accepted && event.isUpdate === false && liveWaiting()) return;
    if (promptedWorker) sawControl = true;
    if (accepted) {
      reloadForUpdate();
      return;
    }
    if (heldForUnsaved) {
      options.onNeedRefresh();
      return;
    }
    dismiss();
  });

  const search = options.locationSearch ?? globalThis.location?.search ?? "";
  if (
    mode === "e2e" &&
    !reloaded &&
    new URLSearchParams(search).has("pwa-update")
  ) {
    showPrompt();
  }

  const inspect = (registration: AppUpdateRegistration) => {
    if (registration.waiting) showPrompt(registration.waiting);
    const installing = registration.installing;
    if (!installing || armed.has(installing)) return;
    armed.add(installing);
    installing.addEventListener("statechange", () => {
      if (installing.state !== "installed") return;
      if (!pageControlled()) return;
      showPrompt(installing);
    });
  };

  const watchRegistration = (
    registration: AppUpdateRegistration | undefined,
  ) => {
    if (!options.watch || !registration) return;
    let lastCheckAt = Number.NEGATIVE_INFINITY;
    const check = () => {
      if (navigator.onLine === false) return;
      const now = Date.now();
      if (now - lastCheckAt < UPDATE_CHECK_GAP_MS) return;
      lastCheckAt = now;
      try {
        void Promise.resolve(registration.update())
          .then(() => {
            inspect(registration);
          })
          .catch(() => {
            // The tab can outlive a worker that was replaced mid-check.
          });
      } catch {
        // update() can throw before it returns a promise.
      }
    };
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") check();
    });
    window.addEventListener("focus", check);
    window.addEventListener("pageshow", check);
    window.addEventListener("online", check);
    window.addEventListener("pointerdown", check);
    window.addEventListener("keydown", check);
    window.setInterval(check, UPDATE_CHECK_OPEN_MS);
  };

  void worker
    .register({ immediate: true })
    .then((registration) => {
      registrationRef = registration;
      if (registration?.waiting) activateOnBoot(registration.waiting);
      watchRegistration(registration);
    })
    .catch(() => {
      // Without a worker the app still loads from the network.
    });

  return {
    acceptUpdate() {
      if (accepted || reloadStarted) return;
      accepted = true;
      userAccepted = true;
      // The e2e build has no waiting worker; reload here so the button is
      // observable. One reload, even if `controlling` also fires.
      if (mode === "e2e") {
        worker.messageSkipWaiting();
        reloadForUpdate();
        return;
      }
      // Already activated before the click: `registration.waiting` is empty
      // and `controlling` has fired, so SKIP_WAITING would land nowhere.
      if (alreadyApplied()) {
        reloadForUpdate();
        return;
      }
      worker.messageSkipWaiting();
    },
  };
}
